import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { ChatProposalAction } from "@mapmycustomers/shared/types/chat/ChatContent";
import { AnyEntityId } from "@mapmycustomers/shared/types/entity";

// One record an applied proposal changed, in the vocabulary uiSync speaks.
export interface ChatChangedRecord {
  entityType: EntityType;
  id: AnyEntityId;
}

// log_activity carries its parent as the raw wire strings rather than as EntityType. The two
// happen to share their values, so this map is what makes the equivalence deliberate instead of a
// cast that would keep compiling if either side ever moved.
const LOG_ACTIVITY_PARENT_TYPES: Record<
  NonNullable<Extract<ChatProposalAction, { type: "log_activity" }>["entityType"]>,
  EntityType
> = {
  accounts: EntityType.COMPANY,
  contacts: EntityType.PERSON,
  deals: EntityType.DEAL,
};

// Which records the platform touched when this proposal applied, so the rest of the app can
// refetch them. One entry per action type, beside the serializer in proposalApply.ts that decides
// what each one sends — a new proposal story adds to both.
//
// Every type reports the PARENT record rather than whatever the apply created. A logged activity
// and a written note are new rows of their own, but what goes stale on screen is the record they
// hang off: its last-contact date, its pin colour, its notes list. Reporting the created row
// instead would need its full entity, which the apply response does not return — it carries an id
// alone — and would still leave the parent stale.
const resolveProposalChangedRecords = (action: ChatProposalAction): ChatChangedRecord[] => {
  switch (action.type) {
    case "change_deal_stage":
      // entityType on the wire is the message's context, which for a deal move is not always the
      // Deal itself. The record that moved is, by definition, a Deal.
      return [{ id: action.entityId, entityType: EntityType.DEAL }];
    // It creates a record rather than updating one; the created record and any linked Company or
    // Person it announces are handled in sagas.ts, which has the draft this switch does not.
    case "create_record":
      return [];
    // It creates a route and changes no existing record; the saga announces the new route itself,
    // from the apply result's createdEntityId.
    case "create_route":
      return [];
    case "log_activity": {
      // All three parent keys travel together and are absent for a standalone activity, which
      // belongs to no record and so leaves nothing on screen stale.
      const entityType = action.entityType
        ? LOG_ACTIVITY_PARENT_TYPES[action.entityType]
        : undefined;
      return entityType && action.entityId !== undefined
        ? [{ id: action.entityId, entityType }]
        : [];
    }
    case "log_note":
    case "record_update":
      return [{ id: action.entityId, entityType: action.entityType }];
    case "manage_relationship":
      // Every end of the link, not just the anchor: the platform may write the related record
      // rather than the one the action names — an account.contacts link writes the Person — and on
      // an account.deals move the previous holder loses the Deal it used to show.
      return [
        { id: action.entityId, entityType: action.entityType },
        ...(action.relatedEntity
          ? [{ id: action.relatedEntity.id, entityType: action.relatedEntity.entityType }]
          : []),
        ...(action.previousEntity
          ? [{ id: action.previousEntity.id, entityType: action.previousEntity.entityType }]
          : []),
      ];
    // The route, not the stop's Company or Person: the record's own fields do not change, and a
    // manual stop edit reports only the route too — so reporting more here would make the chat
    // behave differently from the rest of the app.
    case "manage_route_stops":
    case "manage_route_stops_pick":
      return [{ id: action.routeId, entityType: EntityType.ROUTE }];
    // send_email and the unknown sentinel change no CRM record we can name. An email may become an
    // activity later, but not on this apply, and inventing a record here would refetch for nothing.
    default:
      return [];
  }
};

// Grouped by entity type and de-duplicated, because uiSync is subscribed to per type and a bundled
// message can carry several proposals against one record — two edits to the same Company are one
// refetch, not two.
export const groupChangedRecords = (
  records: ChatChangedRecord[]
): { entityType: EntityType; updatedIds: AnyEntityId[] }[] => {
  const byType = new Map<EntityType, AnyEntityId[]>();
  records.forEach(({ id, entityType }) => {
    const ids = byType.get(entityType) ?? [];
    if (!ids.includes(id)) {
      byType.set(entityType, [...ids, id]);
    }
  });
  return [...byType].map(([entityType, updatedIds]) => ({ entityType, updatedIds }));
};

export default resolveProposalChangedRecords;
